import { AutoComplete } from 'primereact/autocomplete';
import { Toast } from 'primereact/toast';
import { classNames } from 'primereact/utils';
import { Control, FieldPath, useController } from 'react-hook-form';
import { itemsAreAllVirtual } from 'utils/cartUtils';
import { useCartContext } from 'CartContext';
import { ReactElement, useState, useEffect, useRef } from 'react';
import ErrorLabel from '../ErrorLabel';
import { CountryOrStateValues } from './CountryAndStateDropdownField';

export interface StateDropdownFieldProps<TFieldValues> {
  control: Control<TFieldValues>;
  selectedCountryCode: string | undefined;
}

export default function StateDropdown<TFieldValues>(
  props: StateDropdownFieldProps<TFieldValues>,
) {
  const { control, selectedCountryCode } = props;

  // states
  const [states, setStates] = useState<CountryOrStateValues[]>([]);
  const [localState, setLocalState] = useState<string>('');
  const [allStates, setAllStates] = useState<CountryOrStateValues[]>([]);
  const [selectedState, setSelectedState] = useState<
    CountryOrStateValues | undefined
  >();

  // hooks
  const { items } = useCartContext();
  const toast = useRef<any>(null);
  const {
    field,
    fieldState,
    formState: { errors },
  } = useController({
    name: 'stateRegion' as FieldPath<TFieldValues>,
    control,
    rules: allStates.length
      ? {
          // If there are states in this country, it's required:
          required: 'State is required.',
          // and it must be a valid state:
          validate: {
            invalidStateCode: v =>
              isValidStateCode(v as string | FieldPath<TFieldValues>),
          },
        }
      : {},
  });

  // computed values
  const virtualItemsOnly = itemsAreAllVirtual(items);

  // * If country/new country is selected, update state/region list.
  useEffect(() => {
    if (selectedCountryCode) {
      import(`../../../assets/states/${selectedCountryCode.toUpperCase()}`)
        .then(data => {
          setStates(data.default);
          setAllStates(data.default);
          setLocalState('');

          if (field.value && typeof field.value === 'string') {
            // *Allows browser autofill to set selectedState:
            const autofillState = data.default.find(
              (state: { code: string; name: string }) =>
                state.code.toLowerCase() ===
                  String(field.value).toLowerCase() ||
                state.name.toLowerCase() === String(field.value).toLowerCase(),
            );
            setSelectedState(autofillState);
            field.onChange(autofillState);
          }
        })
        .catch(e => {
          console.log(
            `no states for country code ${selectedCountryCode}, error ${e}`,
          );
          setSelectedState(undefined);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountryCode]);

  useEffect(() => {
    if (selectedCountryCode) {
      const doesStateExist = allStates.filter(
        state =>
          state.code.toLowerCase() === selectedState?.code.toLowerCase() ||
          state.name.toLowerCase() === selectedState?.name.toLowerCase(),
      );

      // * If selectedCountryCode changes, clear the state/region input field:
      if (!doesStateExist.length && selectedState) {
        setSelectedState(undefined);
        field.onChange(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStates]);

  // *Helps with browser autofill
  useEffect(() => {
    const stateInput =
      document.getElementById('stateRegion')?.firstElementChild;
    if (stateInput) {
      // https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
      stateInput.setAttribute('autocomplete', 'address-level1');
    }
  }, []);

  useEffect(() => {
    if (localState) {
      const findState = states.find(
        state => state.code.toLowerCase() === localState.toLowerCase(),
      );
      setSelectedState(findState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localState]);

  const stateOptionTemplate = ({
    name,
    code,
  }: CountryOrStateValues): ReactElement => (
    <div className="flex align-items-center">
      <div className="mr-3">{name}</div>
      <div>({code})</div>
    </div>
  );

  const isValidStateCode = (
    stateCode: string | Record<string, string>,
  ): boolean =>
    // *validate stateCode if typed or selected
    allStates.find(
      state =>
        state.code.toLowerCase() ===
        (typeof stateCode === 'string'
          ? stateCode?.toLowerCase()
          : stateCode.code.toLowerCase()),
    ) !== undefined;

  const searchState = (event: { query: string }, onChange: any): void => {
    /// * find the first state that matches user input:
    const findState: CountryOrStateValues | undefined = allStates.find(
      state =>
        state.code.toLowerCase() === event.query.toLowerCase() ||
        state.name.toLowerCase() === event.query.toLowerCase(),
    );
    // * find an array of states that match query:
    const filteredStates = allStates.filter(
      state =>
        state.code.toLowerCase().includes(event.query.toLowerCase()) ||
        state.name.toLowerCase().includes(event.query.toLowerCase()),
    );

    // *SetTimeout gives a chance for the user to type out the state name and show the loading spinner:
    setTimeout(() => {
      if (findState) {
        onChange(findState);
        setSelectedState(findState);
        setStates([]);
      }

      if (findState && filteredStates.length === 1) {
        setStates([]);
      } else if (filteredStates.length > 1 || filteredStates.length === 0) {
        const fuzzyMatch = allStates.filter(
          state =>
            state.name.toLowerCase().includes(event.query.toLowerCase()) ||
            state.code.toLowerCase().includes(event.query.toLowerCase()),
        );
        setStates(fuzzyMatch);
      } else {
        setStates(filteredStates);
      }
    }, 250);
  };

  // ? Modal/toast warnings for user (this can be removed once form progress indicators are added):
  const noCountrySelected = (): void => {
    toast.current?.show({
      severity: 'error',
      summary: 'No country selected',
      detail: 'Please select a country first',
      life: 3000,
    });
  };
  const countryWithoutStates = (): void => {
    toast.current?.show({
      severity: 'info',
      summary: 'No states/regions available',
      detail: `No states/regions associated with selected country ${selectedCountryCode}`,
      life: 3000,
    });
  };

  const showWarningToast = (): void => {
    if (!allStates.length && !selectedCountryCode) {
      noCountrySelected();
    }
    if (selectedCountryCode && !allStates.length) {
      countryWithoutStates();
    }
  };

  return (
    <div className="p-field">
      <span className="p-float-label">
        <Toast
          ref={toast}
          position="bottom-center"
          style={{ marginBottom: '7rem' }}
        />
        <AutoComplete
          {...field}
          id={field.name}
          inputClassName={`${
            selectedState?.code ? 'border-2 border-teal-300' : null
          }`}
          value={selectedState || field.value}
          suggestions={states}
          completeMethod={e => searchState(e, field.onChange)}
          field="code"
          dropdown
          autoHighlight
          forceSelection
          ref={field.ref}
          style={{ width: '100%' }}
          onChange={e => {
            let inputValue = e.value;
            if (typeof e.value === 'string' && e.value.length >= 11) {
              inputValue = allStates.find(
                state =>
                  state.name.toLowerCase() === e.value.toLowerCase() ||
                  state.code.toLowerCase() === e.value.toLowerCase(),
              );
            }
            setSelectedState(inputValue);
            field.onChange(e.value);
          }}
          onKeyUp={e => {
            setLocalState((e.target as HTMLInputElement).value);
          }}
          onFocus={() => showWarningToast()}
          itemTemplate={stateOptionTemplate}
          // * If user adds a physical product and tries to checkout. Country will be disabled/set to only United States while disabling the state dropdown to have only have US states.
          disabled={
            !virtualItemsOnly && selectedCountryCode?.toLowerCase() !== 'us'
          }
          className={classNames({ 'p-invalid': fieldState.error })}
        />

        <label
          htmlFor="stateRegion"
          className={classNames({
            // ? Is there a better way to do this?
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            'p-error': errors.stateRegion,
          })}
        >
          State/Province/Region
          <span
            style={{
              fontWeight: 'bold',
              fontSize: '1.3rem',
              color: '#14a8a6',
            }}
          >
            *
          </span>
        </label>
      </span>
      <ErrorLabel errors={errors} name="stateRegion" />
    </div>
  );
}
